import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, ResponsiveContainer, Tooltip, Legend,
  XAxis, YAxis, CartesianGrid
} from 'recharts';
import './Reports.css';
import EmailReportPage from './Email';
import CouponAnalytics from './CouponAnalytics';

// --- API CONFIGURATION ---
const USER_API_BASE = process.env.REACT_APP_LOGIN_APP_API_BASE;
const PAYMENT_API_BASE = process.env.REACT_APP_API_URL;
const ANALYTICS_API_BASE = process.env.REACT_APP_API_URL;
const PAGE_LIMIT = 10;

// --- REUSABLE UI COMPONENTS (Unchanged) ---
const MetricCard = ({ title, value, change }) => (
  <div className="analytics-page-metric-card">
    <p className="analytics-page-metric-card__title">{title}</p>
    <p className="analytics-page-metric-card__value">{value}</p>
    {change && <p className="analytics-page-metric-card__change">{change}</p>}
  </div>
);
const ChartCard = ({ title, children }) => (
  <div className="analytics-page-chart-card">
    <h3 className="analytics-page-chart-card__title">{title}</h3>
    <div className="analytics-page-chart-card__content">{children}</div>
  </div>
);
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  return (
    <div className="analytics-page-pagination">
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
      <span>Page {currentPage} of {totalPages}</span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
    </div>
  );
};
const CustomDataTable = ({ title, headers, children }) => (
  <div className="analytics-page-data-table-wrapper">
    {title && <h3 className="analytics-page-data-table__title">{title}</h3>}
    <div className="analytics-page-data-table-container">
      <table className="analytics-page-data-table">
        <thead><tr>{headers.map(h => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  </div>
);

// --- NAVIGATION & MAIN CONTENT COMPONENTS ---
const LeftSection = ({ activeSection, setActiveSection }) => {
  const sections = [
    { key: 'student_analytics', label: 'Student Analytics' },
    { key: 'counselling_and_sessions', label: 'Counselling & Sessions' },
    { key: 'reports_and_forms', label: 'Reports & Forms' },
    { key: 'coupon_analytics', label: 'Coupon Analytics' },
    { key: 'email_analytics', label: 'Email Report Analytics' }
  ];
  return (
    <nav className="analytics-page-left-section">
      <h2 className="analytics-page-left-section__title">ANALYTICS</h2>
      <ul className="analytics-page-left-section__nav">
        {sections.map(section => (
          <li key={section.key} className={`analytics-page-left-section__nav-item ${activeSection === section.key ? 'is-active' : ''}`} onClick={() => setActiveSection(section.key)}>
            {section.label}
          </li>
        ))}
      </ul>
    </nav>
  );
};

const MainContent = ({ section, data, loading, error, onPageChange }) => {
    if (loading) return <div className="analytics-page-message-center">Loading...</div>;
    if (error) return <div className="analytics-page-message-center is-error">⚠ {error}</div>;

    const { kpis, chartData, tableData } = data;
    const PIE_COLORS = ['#10b981', '#4f46e5', '#06b6d4'];
    const formatCurrency = (amount) => `₹${(amount / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

    const renderSection = () => {
        switch (section) {
            case 'student_analytics':
                return (
                    <div className="analytics-page-section">
                        <CustomDataTable title="ALL REGISTERED USERS" headers={['User ID', 'First Name', 'Last Name', 'Email', 'Phone']}>
                        {(tableData.students.data || []).map(u => ( <tr key={u.userId || u._id}><td>{u.userId}</td><td>{u.firstName}</td><td>{u.lastName}</td><td>{u.email}</td><td>{u.phone}</td></tr> ))}
                        </CustomDataTable>
                        <Pagination {...tableData.students} onPageChange={(page) => onPageChange('students', page)} />
                    </div>
                );
            case 'counselling_and_sessions':
                return (
                    <>
                        <div className="analytics-page-section">
                            <h2 className="analytics-page-section__title">COUNSELLING TRANSACTIONS</h2>
                            <div className="analytics-page-grid is-cols-5"><MetricCard title="TOTAL COUNSELLINGS" value={kpis.totalCounsellings || 0} /><MetricCard title="PURCHASE REVENUE" value={formatCurrency(kpis.netRevenue || 0)} /><MetricCard title="PURCHASE RATE" value={`${kpis.purchaseRate || '0.00'}%`} /><MetricCard title="REFUNDED COUNSELLINGS" value={kpis.refundedCounsellings || 0} /><MetricCard title="TOP PACKAGE" value={chartData.mostUsedPackage?.name || 'N/A'} change={`${chartData.mostUsedPackage?.sales || 0} sales`} /></div>
                            <CustomDataTable title="ALL COUNSELLING PAYMENTS" headers={['Payment ID', 'User ID', 'Amount', 'Package', 'Status', 'Date']}>
                            {(tableData.counsellingPayments.data || []).map(p => ( <tr key={p._id}><td>{p.paymentId || 'N/A'}</td><td>{p.userId}</td><td>{formatCurrency(p.amount)}</td><td>1-on-1 Counselling</td><td>{p.status}</td><td>{new Date(p.createdAt).toLocaleDateString()}</td></tr> ))}
                            </CustomDataTable>
                            <Pagination {...tableData.counsellingPayments} onPageChange={(page) => onPageChange('counsellingPayments', page)} />
                        </div>
                        <div className="analytics-page-section">
                            <h2 className="analytics-page-section__title">SESSION ANALYTICS</h2>
                            <div className="analytics-page-grid is-cols-2"><ChartCard title="Sessions Over Time"><ResponsiveContainer width="100%" height={300}><LineChart data={chartData.sessionsTrend}><CartesianGrid /><XAxis dataKey="date" /><YAxis allowDecimals={false} /><Tooltip /><Legend /><Line type="monotone" dataKey="sessions" stroke="#8884d8" /></LineChart></ResponsiveContainer></ChartCard><ChartCard title="Engaged Sessions Distribution"><ResponsiveContainer width="100%" height={300}><PieChart><Pie data={chartData.engagedSessions} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={100} label>{(chartData.engagedSessions || []).map((_, index) => <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />)}</Pie><Tooltip /><Legend /></PieChart></ResponsiveContainer></ChartCard></div>
                            <CustomDataTable title="ALL SESSIONS" headers={['Session ID', 'User ID', 'Status', 'Scheduled At', 'Created At']}>
                            {(tableData.sessions.data || []).map(s => ( <tr key={s._id}><td>{s._id}</td><td>{s.userId}</td><td>{s.status}</td><td>{new Date(s.scheduledAt).toLocaleString()}</td><td>{new Date(s.createdAt).toLocaleDateString()}</td></tr> ))}
                            </CustomDataTable>
                            <Pagination {...tableData.sessions} onPageChange={(page) => onPageChange('sessions', page)} />
                        </div>
                    </>
                );
            case 'reports_and_forms':
                const { formPaymentsByType, totalFormRevenue } = chartData;
                return (
                    <>
                        <div className="analytics-page-section">
                            <h2 className="analytics-page-section__title">STUDENT REPORTS</h2>
                            <div className="analytics-page-grid is-cols-2"><MetricCard title="TOTAL USERS" value={kpis.totalUsers || 0} /><MetricCard title="TOTAL REPORTS GENERATED" value={kpis.totalReportsGenerated || 0} /></div>
                            <ChartCard title="Reports Generated by Package"><ResponsiveContainer width="100%" height={300}><BarChart data={chartData.reportsByPackage}><CartesianGrid /><XAxis dataKey="name" /><YAxis allowDecimals={false} /><Tooltip /><Legend /><Bar dataKey="count" name="Reports" fill="#ffc658" /></BarChart></ResponsiveContainer></ChartCard>
                            <CustomDataTable title="GENERATED REPORTS" headers={['Report ID', 'User ID', 'Package Type', 'Date']}>
                                {(tableData.reports.data || []).map(r => ( <tr key={r._id}><td>{r._id}</td><td>{r.userId}</td><td>{r.packageType}</td><td>{new Date(r.createdAt).toLocaleDateString()}</td></tr> ))}
                            </CustomDataTable>
                            <Pagination {...tableData.reports} onPageChange={(page) => onPageChange('reports', page)} />
                        </div>
                        <div className="analytics-page-section">
                            <h2 className="analytics-page-section__title">FORM PAYMENT ANALYTICS</h2>
                            <div className="analytics-page-grid is-cols-2"><MetricCard title="TOTAL FORM PAYMENTS" value={(chartData.allFormPayments || []).length} /><MetricCard title="TOTAL REVENUE FROM FORMS" value={`₹${(totalFormRevenue || 0).toFixed(2)}`} /></div>
                            <ChartCard title="Payments by Form Type"><ResponsiveContainer width="100%" height={300}><BarChart data={formPaymentsByType}><CartesianGrid /><XAxis dataKey="name" /><YAxis /><Tooltip /><Legend /><Bar dataKey="count" name="Payments" fill="#17a2b8" /></BarChart></ResponsiveContainer></ChartCard>
                            <CustomDataTable title="ALL FORM PAYMENTS" headers={['Payment ID', 'User ID', 'Amount', 'Form Type', 'Status', 'Date']}>
                                {(tableData.formPayments.data || []).map(p => ( <tr key={p._id}><td>{p.razorpay_payment_id || 'N/A'}</td><td>{p.userId}</td><td>₹{p.amount}</td><td>{p.formType || 'Standard Form'}</td><td>{p.status}</td><td>{new Date(p.createdAt).toLocaleDateString()}</td></tr> ))}
                            </CustomDataTable>
                            <Pagination {...tableData.formPayments} onPageChange={(page) => onPageChange('formPayments', page)} />
                        </div>
                    </>
                );
            default:
                return <div className="analytics-page-message-center">Please select a section.</div>;
        }
    };
    return <div className="analytics-page-content-area">{renderSection()}</div>;
};

// --- MAIN PAGE COMPONENT ---
const Reports = () => {
  const [activeSection, setActiveSection] = useState('counselling_and_sessions');
  const [kpis, setKpis] = useState({});
  const [chartData, setChartData] = useState({});
  const [tableData, setTableData] = useState({
    students: { data: [], currentPage: 1, totalPages: 1 },
    counsellingPayments: { data: [], currentPage: 1, totalPages: 1 },
    reports: { data: [], currentPage: 1, totalPages: 1 },
    sessions: { data: [], currentPage: 1, totalPages: 1 },
    formPayments: { data: [], currentPage: 1, totalPages: 1 },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const handlePageChange = useCallback((dataType, newPage) => {
    setTableData(prev => ({ ...prev, [dataType]: { ...prev[dataType], currentPage: newPage }}));
  }, []);

  useEffect(() => {
    if (['coupon_analytics', 'email_analytics'].includes(activeSection)) {
        setLoading(false);
        return;
    }

    const token = localStorage.getItem('adminToken');
    if (!token) {
      setError("Authentication token not found.");
      return;
    }
    const authHeaders = { headers: { 'Authorization': `Bearer ${token}` } };
    
    const fetchAllData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [ analyticsRes, counsellingRes, sessionsRes, reportsRes, formsRes, studentsRes ] = await Promise.all([
          fetch(`${ANALYTICS_API_BASE}/api/analytics/summary`, authHeaders),
          fetch(`${PAYMENT_API_BASE}/api/payments/counselling?page=${tableData.counsellingPayments.currentPage}&limit=${PAGE_LIMIT}`, authHeaders),
          fetch(`${PAYMENT_API_BASE}/api/sessions?page=${tableData.sessions.currentPage}&limit=${PAGE_LIMIT}`, authHeaders),
          fetch(`${PAYMENT_API_BASE}/api/reports?page=${tableData.reports.currentPage}&limit=${PAGE_LIMIT}`, authHeaders),
          fetch(`${PAYMENT_API_BASE}/api/payments`, authHeaders),
          fetch(`${USER_API_BASE}/api/users?page=${tableData.students.currentPage}&limit=${PAGE_LIMIT}`, authHeaders),
        ]);

        const analyticsData = await analyticsRes.json();
        const allCounsellingData = await counsellingRes.json();
        const allSessionsData = await sessionsRes.json();
        const allReportsData = await reportsRes.json();
        const allFormsData = await formsRes.json();
        const studentsData = await studentsRes.json();
        
        // --- FIX: Correctly define all array variables and handle API responses ---
        const counsellingArray = Array.isArray(allCounsellingData) ? allCounsellingData : [];
        const sessionsArray = Array.isArray(allSessionsData) ? allSessionsData : [];
        const formsArray = Array.isArray(allFormsData) ? allFormsData : [];
        const reportsArray = allReportsData.reports || [];
        const studentsArray = studentsData.users || [];
        
        setKpis(analyticsData.kpis || {});

        // --- FIX: Use the corrected array variables for all calculations ---
        const revenueByDate = counsellingArray.filter(p => p.status === 'success').reduce((acc, p) => { const date = new Date(p.createdAt).toLocaleDateString('en-CA'); acc[date] = (acc[date] || 0) + p.amount; return acc; }, {});
        const sessionsByDate = sessionsArray.reduce((acc, s) => { const date = new Date(s.createdAt).toLocaleDateString('en-CA'); acc[date] = (acc[date] || 0) + 1; return acc; }, {});
        const statusCounts = sessionsArray.reduce((acc, s) => { acc[s.status] = (acc[s.status] || 0) + 1; return acc; }, {});
        const reportsByPkg = reportsArray.reduce((acc, r) => { acc[r.packageType] = (acc[r.packageType] || 0) + 1; return acc; }, {});
        const successfulForms = formsArray.filter(p => p.status === 'success');
        const formPaymentsByType = successfulForms.reduce((acc, p) => { const type = p.formType || 'Standard'; acc[type] = (acc[type] || 0) + 1; return acc; }, {});

        setChartData({
            revenueTrend: Object.entries(revenueByDate).map(([date, revenue]) => ({ date, revenue })).sort((a,b)=>new Date(a.date)-new Date(b.date)),
            salesByPackage: [{ name: '1-on-1 Counselling', sales: analyticsData.kpis?.successfulSales || 0 }],
            mostUsedPackage: { name: '1-on-1 Counselling', sales: analyticsData.kpis?.successfulSales || 0 },
            sessionsTrend: Object.entries(sessionsByDate).map(([date, count]) => ({ date, sessions: count })).sort((a,b)=>new Date(a.date)-new Date(b.date)),
            engagedSessions: Object.entries(statusCounts).map(([name, value]) => ({ name, value })),
            reportsByPackage: Object.entries(reportsByPkg).map(([name, count]) => ({ name, count })),
            allFormPayments: successfulForms,
            totalFormRevenue: successfulForms.reduce((sum, p) => sum + p.amount, 0),
            formPaymentsByType: Object.entries(formPaymentsByType).map(([name, count]) => ({ name, count })),
        });
        
        setTableData(prev => ({
          ...prev,
          students: { data: studentsArray, currentPage: studentsData.currentPage || 1, totalPages: studentsData.totalPages || 1 },
          counsellingPayments: { data: counsellingArray, currentPage: prev.counsellingPayments.currentPage, totalPages: Math.ceil((counsellingArray.length)/PAGE_LIMIT) || 1 },
          sessions: { data: sessionsArray, currentPage: prev.sessions.currentPage, totalPages: Math.ceil((sessionsArray.length)/PAGE_LIMIT) || 1 },
          reports: { data: reportsArray, currentPage: allReportsData.pagination?.currentPage || 1, totalPages: allReportsData.pagination?.totalPages || 1 },
          formPayments: { data: successfulForms.slice((prev.formPayments.currentPage-1)*PAGE_LIMIT, prev.formPayments.currentPage*PAGE_LIMIT), currentPage: prev.formPayments.currentPage, totalPages: Math.ceil(successfulForms.length/PAGE_LIMIT) || 1 }
        }));

      } catch (err) {
        console.error("Data fetching error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
    
  // --- FIX: Add all necessary pagination dependencies ---
  }, [activeSection, tableData.students.currentPage, tableData.counsellingPayments.currentPage, tableData.reports.currentPage, tableData.sessions.currentPage, tableData.formPayments.currentPage]);

  const combinedData = { kpis, chartData, tableData };

  return (
    <div className="analytics-page">
      <Helmet><title>Analytics Dashboard | Admin</title></Helmet>
      <LeftSection activeSection={activeSection} setActiveSection={setActiveSection} />
      <main className="analytics-page-main">
        <header className="analytics-page-header">
          <h1 className="analytics-page-header__title">
            {activeSection.includes('coupon') ? 'Coupon Analytics' : activeSection.includes('email') ? 'Email Analytics' : 'Dashboard'}
          </h1>
        </header>
        <div className="analytics-page-content-area">
          {activeSection === 'coupon_analytics' && <CouponAnalytics />}
          {activeSection === 'email_analytics' && <EmailReportPage />}
          {!['coupon_analytics', 'email_analytics'].includes(activeSection) && (
            <MainContent section={activeSection} data={combinedData} loading={loading} error={error} onPageChange={handlePageChange} />
          )}
        </div>
      </main>
    </div>
  );
};

export default Reports;